import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { generatePresignedUrlForAccess, generatePresignedUrlForProcessContentAccess } from '../../helper/content_helper';

const VideoPlayer = ({ videoQualities }) => {
    const videoRef = useRef(null);
    const videoContainerRef = useRef(null);
    const qualityMenuRef = useRef(null);
    const controlsTimeoutRef = useRef(null);

    const [currentQuality, setCurrentQuality] = useState('360p');
    const [hlsInstance, setHlsInstance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showQualityMenu, setShowQualityMenu] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState(false);

    // Initialize HLS.js player
    useEffect(() => {
        const video = videoRef.current;

        if (Hls.isSupported()) {
            const hls = new Hls({
                maxMaxBufferLength: 30,
                enableWorker: true,
                abrEwmaDefaultEstimate: 500000,
                abrBandWidthFactor: 0.95,
                abrBandWidthUpFactor: 0.7,
            });

            setHlsInstance(hls);

            return () => {
                hls.destroy();
            };
        }
    }, []);

    // Track play/pause state and duration
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleLoadedMetadata = () => setDuration(video.duration);

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, []);

    // Detect fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Skip if focus is on an input element
            if (document.activeElement.tagName === 'INPUT') return;

            switch (e.key.toLowerCase()) {
                case 'f': // F key for fullscreen
                    toggleFullscreen();
                    break;
                case ' ': // Space for play/pause
                    e.preventDefault(); // Prevent page scroll
                    togglePlayPause();
                    break;
                case 'arrowleft': // Left arrow for rewind 5s
                    seekRelative(-5);
                    break;
                case 'arrowright': // Right arrow for forward 5s
                    seekRelative(5);
                    break;
                case 'm': // M key for mute toggle
                    toggleMute();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Handle mouse movement for showing/hiding controls
    useEffect(() => {
        const container = videoContainerRef.current;
        if (!container) return;

        const handleMouseEnter = () => {
            setIsMouseOver(true);
            showControlsTemporarily();
        };

        const handleMouseLeave = () => {
            setIsMouseOver(false);
            hideControls();
        };

        const handleMouseMove = () => {
            showControlsTemporarily();
        };

        container.addEventListener('mouseenter', handleMouseEnter);
        container.addEventListener('mouseleave', handleMouseLeave);
        container.addEventListener('mousemove', handleMouseMove);

        return () => {
            container.removeEventListener('mouseenter', handleMouseEnter);
            container.removeEventListener('mouseleave', handleMouseLeave);
            container.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(controlsTimeoutRef.current);
        };
    }, []);

    // Show controls temporarily (with auto-hide after delay)
    const showControlsTemporarily = () => {
        setShowControls(true);
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => {
            if (!isMouseOver) {
                setShowControls(false);
            }
        }, 3000);
    };

    // Hide controls immediately
    const hideControls = () => {
        setShowControls(false);
        clearTimeout(controlsTimeoutRef.current);
    };

    // Load video source based on quality selection
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoQualities) return;

        const loadVideo = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const seekTime = video.currentTime;
                const wasPaused = video.paused;

                if (currentQuality === 'auto') {
                    // For auto quality (HLS adaptive streaming)
                    const masterUrl = await getPresignedUrl(
                        `${videoQualities['1080p'].replace('_1080p.mp4', '')}_master.m3u8`
                    );

                    if (Hls.isSupported() && hlsInstance) {
                        hlsInstance.loadSource(masterUrl);
                        hlsInstance.attachMedia(video);
                        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
                            if (seekTime > 0) video.currentTime = seekTime;
                            if (!wasPaused) video.play().catch(console.error);
                            setIsLoading(false);
                        });
                    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                        video.src = masterUrl;
                        video.addEventListener('loadedmetadata', () => {
                            if (seekTime > 0) video.currentTime = seekTime;
                            if (!wasPaused) video.play().catch(console.error);
                            setIsLoading(false);
                        });
                    }
                } else {
                    // For manual quality selection
                    const url = await getPresignedUrl(videoQualities[currentQuality]);

                    if (Hls.isSupported() && hlsInstance) {
                        hlsInstance.stopLoad();
                        video.src = url;
                        video.addEventListener('loadedmetadata', () => {
                            if (seekTime > 0) video.currentTime = seekTime;
                            if (!wasPaused) video.play().catch(console.error);
                            setIsLoading(false);
                        });
                    } else {
                        video.src = url;
                        video.addEventListener('loadedmetadata', () => {
                            if (seekTime > 0) video.currentTime = seekTime;
                            if (!wasPaused) video.play().catch(console.error);
                            setIsLoading(false);
                        });
                    }
                }
            } catch (err) {
                console.error('Error loading video:', err);
                setError('Failed to load video. Please try another quality.');
                setIsLoading(false);
            }
        };

        loadVideo();
    }, [currentQuality, videoQualities]);

    // Track current playback time
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const updateTime = () => setCurrentTime(video.currentTime);
        video.addEventListener('timeupdate', updateTime);

        return () => {
            video.removeEventListener('timeupdate', updateTime);
        };
    }, []);

    // Handle errors
    const handleError = () => {
        setError('Video playback error. Please try again.');
    };

    // Get presigned URL from backend
    const getPresignedUrl = async (s3Key) => {
        try {
            const response = await generatePresignedUrlForProcessContentAccess({
                name: s3Key,
                type: 'Video'
            });
            return response.presignedUrl;
        } catch (err) {
            console.error('Error getting presigned URL:', err);
            throw err;
        }
    };

    // Toggle play/pause
    const togglePlayPause = () => {
        if (videoRef.current.paused) {
            videoRef.current.play().catch(console.error);
        } else {
            videoRef.current.pause();
        }
        showControlsTemporarily();
    };

    // Seek relative to current time
    const seekRelative = (seconds) => {
        if (videoRef.current) {
            videoRef.current.currentTime += seconds;
            showControlsTemporarily();
        }
    };

    // Toggle mute
    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            showControlsTemporarily();
        }
    };

    // Toggle quality menu
    const toggleQualityMenu = (e) => {
        e.stopPropagation();
        setShowQualityMenu(!showQualityMenu);
        showControlsTemporarily();
    };

    // Toggle fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            videoContainerRef.current?.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
        showControlsTemporarily();
    };

    // Handle clicks outside quality menu
    const handleClickOutsideMenu = (e) => {
        if (qualityMenuRef.current && !qualityMenuRef.current.contains(e.target)) {
            setShowQualityMenu(false);
        }
    };

    return (
        <div
            style={styles.container}
            ref={videoContainerRef}
            onClick={handleClickOutsideMenu}
        >
            {/* Video Player */}
            <div style={styles.videoWrapper}>
                {isLoading && (
                    <div style={styles.loadingOverlay}>
                        <div style={styles.loadingText}>Loading video...</div>
                    </div>
                )}

                <video
                    ref={videoRef}
                    controls={false}
                    style={styles.videoElement}
                    onError={handleError}
                    onContextMenu={(e) => e.preventDefault()}
                    onClick={togglePlayPause}
                />

                {/* Custom Controls Overlay */}
                <div
                    style={{
                        ...styles.controlsOverlay,
                        opacity: showControls ? 1 : 0,
                        cursor: showControls ? 'default' : 'none'
                    }}
                >
                    {/* Center Controls (play/pause button) */}
                    <div style={styles.centerControls}>
                        <button
                            onClick={togglePlayPause}
                            style={styles.playPauseButton}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Bottom Controls (progress bar, time, quality, fullscreen) */}
                    <div style={styles.bottomControls}>
                        <div style={styles.progressBarContainer}>
                            <input
                                type="range"
                                min="0"
                                max={duration || 100}
                                value={currentTime}
                                onChange={(e) => {
                                    if (videoRef.current) {
                                        videoRef.current.currentTime = e.target.value;
                                    }
                                }}
                                style={styles.progressBar}
                                onMouseDown={() => clearTimeout(controlsTimeoutRef.current)}
                                onMouseUp={showControlsTemporarily}
                            />
                        </div>

                        <div style={styles.bottomControlsRow}>
                            <div style={styles.leftControls}>
                                {/* Play/Pause Button */}
                                <button
                                    onClick={togglePlayPause}
                                    style={styles.smallPlayPauseButton}
                                    aria-label={isPlaying ? 'Pause' : 'Play'}
                                >
                                    {isPlaying ? (
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    )}
                                </button>

                                {/* Time Display */}
                                <div style={styles.timeDisplay}>
                                    {formatTime(currentTime)} / {formatTime(duration)}
                                </div>
                            </div>

                            <div style={styles.rightControls}>
                                {/* Volume Control */}
                                <button
                                    onClick={toggleMute}
                                    style={styles.volumeButton}
                                    aria-label={videoRef.current?.muted ? 'Unmute' : 'Mute'}
                                >
                                    {videoRef.current?.muted ? (
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                                        </svg>
                                    )}
                                </button>

                                {/* Quality Selector */}
                                <div style={styles.qualityButtonContainer} ref={qualityMenuRef}>
                                    <button
                                        onClick={toggleQualityMenu}
                                        style={styles.qualityButton}
                                    >
                                        Quality: {currentQuality}
                                    </button>

                                    {/* Quality Dropdown Menu */}
                                    {showQualityMenu && (
                                        <div style={styles.qualityMenu}>
                                            {/* <div
                                                style={{
                                                    ...styles.qualityOption,
                                                    ...(currentQuality === 'auto' ? styles.activeQualityOption : {})
                                                }}
                                                onClick={() => {
                                                    setCurrentQuality('auto');
                                                    setShowQualityMenu(false);
                                                }}
                                            >
                                                Auto
                                            </div> */}
                                            {Object.keys(videoQualities || {}).map(quality => (
                                                <div
                                                    key={quality}
                                                    style={{
                                                        ...styles.qualityOption,
                                                        ...(currentQuality === quality ? styles.activeQualityOption : {})
                                                    }}
                                                    onClick={() => {
                                                        setCurrentQuality(quality);
                                                        setShowQualityMenu(false);
                                                    }}
                                                >
                                                    {quality}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Fullscreen Button */}
                                <button
                                    onClick={toggleFullscreen}
                                    style={styles.fullscreenButton}
                                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                >
                                    {isFullscreen ? (
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
                                            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={styles.errorMessage}>
                    {error}
                    <button
                        onClick={() => setError(null)}
                        style={styles.errorCloseButton}
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    );
};

// Helper function to format time
const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes().toString().padStart(2, '0');
    const ss = date.getUTCSeconds().toString().padStart(2, '0');

    if (hh > 0) {
        return `${hh}:${mm}:${ss}`;
    }
    return `${mm}:${ss}`;
};

// Styles
const styles = {
    container: {
        position: 'relative',
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: '#000',
        overflow: 'hidden',
        ':fullscreen': {
            maxWidth: 'none',
            width: '100%',
            height: '100%'
        }
    },
    videoWrapper: {
        position: 'relative',
        paddingBottom: '56.25%', // 16:9 aspect ratio
        height: 0,
        overflow: 'hidden'
    },
    videoElement: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        cursor: 'pointer'
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 10
    },
    loadingText: {
        color: '#fff',
        fontSize: '18px'
    },
    controlsOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'opacity 0.3s ease, cursor 0.3s ease',
        zIndex: 2,
        pointerEvents: 'none'
    },
    centerControls: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'auto',
        flex: 1
    },
    playPauseButton: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        border: 'none',
        borderRadius: '50%',
        width: '60px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, background-color 0.2s ease',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            transform: 'scale(1.1)'
        }
    },
    smallPlayPauseButton: {
        backgroundColor: 'transparent',
        border: 'none',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        marginRight: '8px'
    },
    bottomControls: {
        padding: '10px',
        pointerEvents: 'auto',
        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)'
    },
    progressBarContainer: {
        width: '100%',
        marginBottom: '8px'
    },
    progressBar: {
        width: '100%',
        height: '4px',
        cursor: 'pointer',
        appearance: 'none',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '2px',
        ':focus': {
            outline: 'none'
        },
        '::-webkit-slider-thumb': {
            appearance: 'none',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#fff',
            cursor: 'pointer'
        }
    },
    bottomControlsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    leftControls: {
        display: 'flex',
        alignItems: 'center'
    },
    rightControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    timeDisplay: {
        color: '#fff',
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        minWidth: '100px',
        textAlign: 'center'
    },
    qualityButtonContainer: {
        position: 'relative'
    },
    qualityButton: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        border: 'none',
        padding: '6px 12px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s ease',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }
    },
    qualityMenu: {
        position: 'absolute',
        bottom: '40px',
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderRadius: '4px',
        overflow: 'hidden',
        minWidth: '120px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
    },
    qualityOption: {
        color: 'white',
        padding: '8px 16px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s ease',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    activeQualityOption: {
        backgroundColor: '#4285f4'
    },
    fullscreenButton: {
        backgroundColor: 'transparent',
        border: 'none',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    volumeButton: {
        backgroundColor: 'transparent',
        border: 'none',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    errorMessage: {
        position: 'absolute',
        left: '50%',
        bottom: '60px',
        transform: 'translateX(-50%)',
        color: '#ff4444',
        backgroundColor: '#ffeeee',
        padding: '10px 20px',
        borderRadius: '4px',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
    },
    errorCloseButton: {
        background: 'none',
        border: 'none',
        color: '#ff4444',
        fontSize: '18px',
        cursor: 'pointer',
        padding: 0
    }
};

export default VideoPlayer;